import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';

type ActiveFilter = {
    id: string;
    label: string;
    clear: () => void;
};

export type WorkItemsEmptyVariant = 'filtered' | 'true-empty' | 'archive-empty';

type WorkItemsEmptyStateProps = {
    variant: WorkItemsEmptyVariant;
    activeFilters: ActiveFilter[];
    onResetFilters: () => void;
    /** When variant is true-empty: whether at least one mailbox is connected */
    mailboxConnected?: boolean;
    onImportClick?: () => void;
    onConnectMailClick?: () => void;
};

const WorkItemsEmptyState: React.FC<WorkItemsEmptyStateProps> = ({
    variant,
    activeFilters,
    onResetFilters,
    mailboxConnected = false,
    onImportClick,
    onConnectMailClick,
}) => {
    if (variant === 'archive-empty') {
        return (
            <Box
                data-testid="WB_EMPTY_ARCHIVE"
                role="region"
                aria-label="Archive empty"
                sx={{ p: 8, textAlign: 'center', border: '2px dashed var(--color-border)', borderRadius: '2rem', m: 2 }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: 'var(--color-text-muted)', mb: 2, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                    Archive is empty
                </Typography>
                <Typography variant="body1" sx={{ color: 'var(--color-text-secondary)', maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
                    Completed and archived items will appear here.
                </Typography>
            </Box>
        );
    }

    if (variant === 'true-empty') {
        return (
            <Box
                data-testid="WB_EMPTY_TRUE"
                role="region"
                aria-label="Workbench empty"
                sx={{ p: 8, textAlign: 'center', border: '2px dashed var(--color-border)', borderRadius: '2rem', m: 2 }}
            >
                <Typography
                    variant="body2"
                    sx={{ color: 'var(--color-text-muted)', mb: 2, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}
                >
                    Nothing here yet
                </Typography>
                <Typography variant="body1" sx={{ color: 'var(--color-text-secondary)', mb: 4, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
                    {mailboxConnected
                        ? 'Items from synced mail and file imports will show up here.'
                        : 'Connect Gmail to sync items from your inbox, or import a file to add work items manually.'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {!mailboxConnected && onConnectMailClick && (
                        <Button
                            variant="contained"
                            onClick={onConnectMailClick}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '12px',
                                px: 3,
                                fontWeight: 700,
                            }}
                        >
                            Connect Gmail
                        </Button>
                    )}
                    {onImportClick && (
                        <Button
                            variant={mailboxConnected ? 'contained' : 'outlined'}
                            onClick={onImportClick}
                            sx={{
                                borderColor: 'var(--color-border)',
                                color: mailboxConnected ? undefined : 'var(--color-text-secondary)',
                                textTransform: 'none',
                                borderRadius: '12px',
                                px: 3,
                                fontWeight: 700,
                                '&:hover': { borderColor: 'var(--color-accent)', bgcolor: 'var(--color-bg-card-hover)' },
                            }}
                        >
                            Import file
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            data-testid="WB_EMPTY_FILTERED"
            role="region"
            aria-label="No matching work items"
            sx={{ p: 8, textAlign: 'center', border: '2px dashed var(--color-border)', borderRadius: '2rem', m: 2 }}
        >
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 3, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                No items match your filters
            </Typography>

            {activeFilters.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 2 }}>
                        Try clearing one of these active filters:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {activeFilters.map((filter) => (
                            <Chip
                                key={filter.id}
                                label={filter.label}
                                onDelete={filter.clear}
                                sx={{
                                    bgcolor: 'rgba(99, 102, 241, 0.1)',
                                    color: '#4f46e5',
                                    border: '1px solid rgba(99, 102, 241, 0.3)',
                                    fontWeight: 600,
                                    '& .MuiChip-deleteIcon': { color: '#4f46e5', opacity: 0.7, '&:hover': { opacity: 1 } },
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {activeFilters.length > 0 && (
                <Button
                    variant="outlined"
                    onClick={onResetFilters}
                    sx={{
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-secondary)',
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 4,
                        '&:hover': { borderColor: 'var(--color-accent)', bgcolor: 'var(--color-bg-card-hover)' },
                    }}
                >
                    Reset all filters
                </Button>
            )}
        </Box>
    );
};

export default WorkItemsEmptyState;
